import { type BigIntish, MathLib } from '@morpho-org/blue-sdk'
import { safeParseNumber } from '@morpho-org/blue-sdk-viem'
import { type ApiInputToken, parseApiAsset } from '@morpho-org/dapp-core'
import { formatUnits } from 'viem'

import type { CollateralExposure } from '@/components/common/CollateralExposureStack'
import type { SimpleAssetFields } from '@/types/fields'

import {
  getVaultV2MarketCaps,
  hasCompleteMarketCapCoverage,
  isVaultV2DustMarket,
  type VaultV2CapStackItem
} from '@/helpers/vaultV2DustMarket'

type PricedCollateralExposure = CollateralExposure & { supplyAssetsUsd: bigint }

export type VaultAllocation = VaultAllocationForCollateralExposure[]

export interface VaultAllocationForCollateralExposure {
  market: {
    collateralAsset: ApiInputToken | null
    loanAsset: ApiInputToken
  }
  supplyAssetsUsd: number | null
}

export interface VaultV2CapsItem extends VaultV2CapStackItem {
  allocation: BigIntish
  data:
    | {
        __typename: 'MarketV1CapData'
        adapterAddress?: string
        market?: {
          marketId: string
          lltv?: BigIntish
          collateralAsset?: SimpleAssetFields | null
        } | null
      }
    | { __typename: 'AdapterCapData'; adapterAddress?: string }
    | { __typename: 'CollateralCapData'; collateralToken?: { address: string } | null }
    | null
}

/**
 * Processes vault market allocations to generate a list of collateral exposures.
 * Each exposure represents a unique collateral token with its USD supply value.
 * Entries without a collateralAsset represent idle liquidity.
 *
 * @param allocation - Array of market allocations from a vault's state, containing information about
 *                    collateral assets, oracles, LTVs, and supply amounts. Entries without collateralAsset
 *                    represent idle liquidity (assets not allocated to any market).
 * @returns Array of CollateralExposure objects sorted by descending supplyAssetsUsd value. Each object contains:
 *          - token: The collateral asset information (address and symbol), or loan asset for idle liquidity
 *          - supplyAssetsUsd: Total USD value of supplied assets for this collateral
 *          - isIdle: True for idle liquidity entries (no collateralAsset)
 */
export const getCollateralExposures = (allocation: VaultAllocation | undefined | null) => {
  if (!allocation) return []

  return allocation
    .reduce<PricedCollateralExposure[]>((acc, marketAllocation) => {
      const { collateralAsset, loanAsset } = marketAllocation.market
      const marketSupplyAssetsUsd = safeParseNumber(marketAllocation.supplyAssetsUsd ?? 0)

      // Determine if this is idle liquidity (no collateralAsset)
      const isIdle = !collateralAsset?.address || !collateralAsset?.symbol
      const asset = isIdle ? loanAsset : collateralAsset

      if (!asset) return acc

      // Find existing exposure for the same asset
      const existingExposure = acc.find(exposure => exposure.token.address === asset.address)

      if (existingExposure) {
        existingExposure.supplyAssetsUsd += marketSupplyAssetsUsd
      } else {
        acc.push({
          token: parseApiAsset(asset),
          supplyAssetsUsd: marketSupplyAssetsUsd,
          isIdle
        })
      }

      return acc
    }, [])
    .toSorted((a, b) => {
      if (b.supplyAssetsUsd > a.supplyAssetsUsd) return 1
      if (b.supplyAssetsUsd < a.supplyAssetsUsd) return -1
      return 0
    })
}

/**
 * Returns all collateral exposures for a Vault V2 by combining and merging exposures from the underlying Vault V1
 * (scaled pro rata by the adapter allocation) and direct Vault V2 cap positions, including idle liquidity.
 *
 * @param vaultV1Adapter Vault V1 adapter info (with allocation and totalAssets)
 * @param vaultV2 Vault V2 info (caps, idleAssetsUsd, and asset)
 * @returns Collateral exposures sorted by descending supplyAssetsUsd.
 * @internal
 */
export const getCollateralExposuresForVaultV2 = ({
  vaultV1Adapter,
  vaultV2
}: {
  vaultV1Adapter:
    | {
        metaMorpho: {
          state: {
            allocation: VaultAllocation | undefined | null
            totalAssets: BigIntish | undefined | null
          } | null
        }
        assets: BigIntish | undefined | null
      }
    | null
    | undefined
  vaultV2: {
    caps: {
      items: VaultV2CapsItem[] | null
    }
    idleAssetsUsd: number | null
    asset: ApiInputToken | null
    /** Tells whether the cap rows cover every market adapter of the vault. */
    adapters?: { items: readonly { __typename?: string; address?: string }[] | null } | null
  }
}): PricedCollateralExposure[] => {
  let exposures: PricedCollateralExposure[] = []

  // Process Vault V1 exposures if available
  if (vaultV1Adapter?.metaMorpho.state) {
    const vaultV1 = vaultV1Adapter.metaMorpho.state
    const vaultV1TotalAssets = BigInt(vaultV1.totalAssets ?? 0)
    const vaultV1Assets = BigInt(vaultV1Adapter.assets ?? 0)

    // Calculate prorata ratio: how much of vault v1's assets are represented in vault v2
    // If vault v1 has no total assets, we can't calculate prorata, so use 0
    const prorataRatio =
      vaultV1TotalAssets && vaultV1TotalAssets > 0n
        ? MathLib.wDivDown(vaultV1Assets, vaultV1TotalAssets)
        : 0n

    // Start with exposures from vault v1 allocation, scaled by prorata
    const vaultV1Exposures = getCollateralExposures(vaultV1.allocation)
    exposures = vaultV1Exposures.map(exposure => ({
      ...exposure,
      supplyAssetsUsd: MathLib.wMulDown(exposure.supplyAssetsUsd, prorataRatio)
    }))
  }

  // Process vault v2 caps to extract collateral exposures
  vaultV2.caps.items?.forEach(cap => {
    // Process MarketV1CapData to get collateral from market
    if (cap.data?.__typename === 'MarketV1CapData') {
      const {
        allocation,
        data: { market }
      } = cap

      if (!market?.collateralAsset || !vaultV2.asset) {
        return
      }

      const { collateralAsset } = market

      const token = parseApiAsset(collateralAsset)
      const loanAssetToken = parseApiAsset(vaultV2.asset)
      const rawAllocationUsd = loanAssetToken.toUsd(BigInt(allocation))
      // Summed across adapters, like the other surfaces: small rows can add up past the raw cap.
      const marketCaps = getVaultV2MarketCaps(vaultV2.caps.items, market.marketId)
      const coverageComplete = hasCompleteMarketCapCoverage(marketCaps, vaultV2.adapters?.items)
      const marketAllocation = marketCaps.reduce(
        (sum, marketCap) => sum + BigInt(marketCap.allocation),
        0n
      )
      const marketAllocationUsd = loanAssetToken.toUsd(marketAllocation)
      const hasKnownPrice = !!loanAssetToken.price

      // Temporary: mirrors VAULT_V2_DUST_FLOOR_USD / isDustAllocation in morpho-api
      // services/consumer (#4733). Remove once these views read from the BFF instead of GraphQL.
      if (
        isVaultV2DustMarket({
          caps: vaultV2.caps.items,
          marketId: market.marketId,
          allocation: coverageComplete ? marketAllocation : null,
          // A zero or missing price is unknown, not $0.
          allocationUsd:
            marketAllocationUsd == null || !hasKnownPrice
              ? null
              : Number(formatUnits(marketAllocationUsd, 18))
        })
      ) {
        return
      }

      const allocationUsd = rawAllocationUsd ?? 0n

      // Find existing exposure with same collateral address
      const existingExposure = exposures.find(
        exposure => exposure.token.address === collateralAsset.address
      )

      if (existingExposure) {
        // Merge: add supplyAssetsUsd values
        existingExposure.supplyAssetsUsd += allocationUsd
      } else {
        // Add new exposure from cap
        exposures.push({
          token,
          supplyAssetsUsd: allocationUsd
        })
      }
    }
  })

  // Add Vault V2's idle liquidity to the existing idle (from V1)
  if (vaultV2.idleAssetsUsd && vaultV2.asset) {
    const v2IdleUsd = safeParseNumber(vaultV2.idleAssetsUsd)
    const idleExposure = exposures.find(exposure => exposure.isIdle)

    if (idleExposure) {
      // Merge V2 idle with existing V1 idle
      idleExposure.supplyAssetsUsd += v2IdleUsd
    } else {
      // No V1 idle, create new idle entry with V2's asset
      exposures.push({
        token: parseApiAsset(vaultV2.asset),
        supplyAssetsUsd: v2IdleUsd,
        isIdle: true
      })
    }
  }

  // Sort by supplyAssetsUsd descending
  return exposures.toSorted((a, b) => {
    if (b.supplyAssetsUsd > a.supplyAssetsUsd) return 1
    if (b.supplyAssetsUsd < a.supplyAssetsUsd) return -1
    return 0
  })
}
